import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// styles
import 'react-toastify/dist/ReactToastify.css';
import './css/pdf.css';

// components
import Modal from '../../components/Modal_3';
import Table from '../Table';

// tools
import { _filterMaterialData } from '../../tools/manager';

// img
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

/**
 * ✅ Nouveau design "APRÈS" (édition)
 * - Lignes: designation(name) / lieu(address) / quantité(quantity) / unité(unit => quantityLabel)
 * - Entête: client / devis-bdc(ctm) / nature globale (description)
 * - IMPORTANT: on aligne l'UPDATE sur la CREATION (PrefillOrder) => même normalisation + _filterMaterialData
 */

const UNIT_OPTIONS = [
  { value: 'U', label: 'U' },
  { value: 'ml', label: 'ml' },
];

// modèle de base pour une nouvelle ligne (shape MaterialData-like)
const emptyMaterial = {
  indexRef: null,
  quantity: '',
  quantityLabel: '',
  address: '',
  name: '',
  description: '',
  surface: '',
  poseAddress: '',
  referenceQr: '',
  unit: 'U', // UI only
};

const EditPrefillWorkOrder = () => {
  const navigate = useNavigate();
  const { idCustomer, idworkorder } = useParams();

  // state
  const [preInter, setPreInter] = useState(null);
  const [customer, setCustomer] = useState();
  const [formError, setFormError] = useState('');
  const [natureIntervention, setNatureIntervention] = useState('');
  const [materialsEdit, setMaterialsEdit] = useState([]);

  const [showModalDeleteLine, setShowModalDeleteLine] = useState(false);
  const [lineToDeleteIndex, setLineToDeleteIndex] = useState(null);
  const [showModalDeleteCoupon, setShowModalDeleteCoupon] = useState(false);

  // store actions
  const getCompany = useStoreActions(actions => actions.middleware.CompanyMiddleware.getCompany);
  const getPreCoupon = useStoreActions(actions => actions.middleware.CouponPreInterMiddleware.getPreCoupon);
  const updateListMaterial = useStoreActions(actions => actions.middleware.CouponPreInterMiddleware.updateListMaterial);
  const deleteInterventionCoupon = useStoreActions(actions => actions.middleware.CouponPreInterMiddleware.deleteInterventioncoupon);

  // ========= HELPERS =========

  const inferUnitFromQuantityLabel = quantityLabel => {
    const ql = String(quantityLabel || '').toLowerCase();
    if (ql.includes('ml')) return 'ml';
    if (ql.includes('u')) return 'U';
    return 'U';
  };

  const inferQtyFromQuantityLabel = quantityLabel => {
    const ql = String(quantityLabel || '').replace(',', '.');
    const match = ql.match(/(\d+(\.\d+)?)/);
    return match ? match[1] : '';
  };

  /**
   * ✅ Transforme une ligne raw (qui peut venir d'anciens formats) en ligne éditable APRÈS
   * - designation: name | designation | material
   * - lieu: address | lieu | pose | depot | poseAddress
   * - nature globale: description | nature
   */
  const toEditableLine = (raw, index) => {
    const unit = raw?.unit || inferUnitFromQuantityLabel(raw?.quantityLabel);

    const qty = raw?.quantity ?? inferQtyFromQuantityLabel(raw?.quantityLabel);

    const name = raw?.name ?? raw?.designation ?? raw?.material ?? '';
    const address =
      raw?.address ??
      raw?.lieu ??
      raw?.pose ??
      raw?.depot ??
      raw?.poseAddress ??
      '';

    const description = raw?.description ?? raw?.nature ?? '';

    return {
      ...emptyMaterial,
      ...raw,
      indexRef: typeof raw?.indexRef === 'number' ? raw.indexRef : index,
      unit,
      quantity: qty === null || qty === undefined ? '' : String(qty),
      name: String(name || ''),
      address: String(address || ''),
      description: String(description || ''),
      quantityLabel: raw?.quantityLabel || '',
    };
  };

  /**
   * ✅ EXACTEMENT comme PrefillOrder
   * - quantity => Float
   * - quantityLabel => "${quantity} ${unit}"
   * - description => natureIntervention (globale)
   * - poseAddress => duplicat du lieu (compat)
   */
  const normalizeLineForSave = (line, index) => {
    const qty = typeof line.quantity === 'string' ? line.quantity.replace(',', '.') : line.quantity;
    const numericQty = qty === '' || qty === null || qty === undefined ? null : Number(qty);

    const safeUnit = line.unit?.trim() || 'U';
    const quantityLabel =
      numericQty && numericQty > 0 ? `${numericQty} ${safeUnit}`.trim() : (line.quantityLabel || '');

    const address = (line.address || '').trim();
    const description = (natureIntervention || line.description || '').trim();

    return {
      indexRef: typeof line.indexRef === 'number' ? line.indexRef : index,

      // ✅ champs "APRÈS"
      name: (line.name || '').trim(),
      address,
      quantity: numericQty,
      quantityLabel,
      description,

      // ✅ compat: on duplique aussi dans poseAddress
      poseAddress: address,

      // optionnels
      surface: (line.surface || '').trim(),
      referenceQr: (line.referenceQr || '').trim() || undefined,
    };
  };

  // ========= API CALLS =========

  const callGetCompany = async () => {
    try {
      const res = await getCompany(idCustomer);
      setCustomer(res);
    } catch (error) {
      toast.error("❌ Une erreur est survenue durant la récupération des données du client");
      window.location.href = '/clients';
    }
  };

  const callGetPreCoupon = async () => {
    try {
      const res = await getPreCoupon(idworkorder);
      setPreInter(res);

      const rawMaterials = Array.isArray(res?.materials) ? res.materials : [];
      const editable = rawMaterials.map(toEditableLine);
      setMaterialsEdit(editable);

      const guessNature =
        editable.find(l => (l.description || '').trim())?.description ||
        editable.find(l => (l.nature || '').trim())?.nature ||
        '';
      setNatureIntervention(guessNature);
    } catch (error) {
      toast.error("❌ Une erreur est survenue durant le chargement des données du bon");
    }
  };

  const callUpdateListMaterial = async (materialsList) => {
    try {
      const normalized = materialsList.map(normalizeLineForSave);

      // ✅ même pipeline que PrefillOrder
      const payload = {
        id: idworkorder,
        materials: _filterMaterialData(normalized),
      };

      await updateListMaterial(payload);
      await callGetPreCoupon();
      setFormError('');
    } catch (error) {
      console.log('update error', error);
      toast.error("❌ Une erreur est survenue durant la modification des données du bon");
    } finally {
      setLineToDeleteIndex(null);
      setShowModalDeleteLine(false);
    }
  };

  const callDeleteCoupon = async () => {
    try {
      await deleteInterventionCoupon(idworkorder);
      navigate(-1);
    } catch (error) {
      toast.error("❌ Une erreur est survenue durant la suppression du bon");
    }
  };

  // ========= UX / VALIDATION =========

  useEffect(() => setFormError(''), [materialsEdit, natureIntervention]);

  const isOkForm = (list) => {
    if (!list?.length) {
      setFormError('Ajouter au moins une ligne.');
      return false;
    }

    for (const element of list) {
      if (!element.name?.trim()) {
        setFormError('Défaut de désignation.');
        return false;
      }

      const qtyStr = String(element.quantity ?? '').replace(',', '.');
      const qty = qtyStr === '' ? NaN : Number(qtyStr);
      if (!Number.isFinite(qty) || qty <= 0) {
        setFormError('Quantité invalide (doit être un chiffre > 0).');
        return false;
      }

      if (!element.unit?.trim()) {
        setFormError("Défaut d'unité (ml ou U).");
        return false;
      }
    }

    return true;
  };

  const changeLineField = (index, key, value) => {
    setMaterialsEdit(prev => {
      const copy = prev.map(item => ({ ...item }));
      const line = copy[index];
      if (!line) return prev;

      line[key] = value;

      // auto-sync quantityLabel à chaque changement qty/unit (comme création)
      if (key === 'quantity' || key === 'unit') {
        const qtyStr = String(line.quantity ?? '').replace(',', '.');
        const qtyNum = qtyStr === '' ? null : Number(qtyStr);
        if (qtyNum && qtyNum > 0) {
          line.quantityLabel = `${qtyNum} ${line.unit || 'U'}`.trim();
        } else {
          line.quantityLabel = '';
        }
      }

      copy[index] = line;
      return copy;
    });
  };

  const addLine = (e) => {
    e.preventDefault();
    setMaterialsEdit(prev => [
      ...prev,
      {
        ...emptyMaterial,
        description: natureIntervention,
        indexRef: prev.length,
      },
    ]);
  };

  const askDeleteLine = (index) => {
    setLineToDeleteIndex(index);
    setShowModalDeleteLine(true);
  };

  const confirmDeleteLine = () => {
    if (lineToDeleteIndex === null || lineToDeleteIndex === undefined) return;

    const copy = [...materialsEdit];
    copy.splice(lineToDeleteIndex, 1);

    // ✅ si plus rien, on remet une ligne vide (sans bloquer par validation)
    const next =
      copy.length
        ? copy
        : [{ ...emptyMaterial, description: natureIntervention, indexRef: 0 }];

    callUpdateListMaterial(next);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!isOkForm(materialsEdit)) return;
    callUpdateListMaterial(materialsEdit);
  };

  // ========= EFFECTS =========

  useEffect(() => {
    callGetCompany();
    callGetPreCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const couponNumero = useMemo(() => preInter?.numero || '', [preInter]);
  const couponCtm = useMemo(() => preInter?.ctm || '', [preInter]);

  // ========= RENDER =========

  return (
    <section className="pt-14">
      {/* Entête (APRÈS) */}
      <div className="flex flex-col lg:flex-row lg:space-x-8 mx-14">
        <div className="flex flex-col space-y-4">
          <label>Nom du CLIENT</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {customer?.name}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <label>DEVIS / BDC</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {couponCtm}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <label>Numéro du bon</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {couponNumero}
          </p>
        </div>

        <div className="flex flex-col space-y-4">
          <label>Nature de l’intervention</label>
          <input
            className="w-[260px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin input"
            type="text"
            placeholder="nature de l’intervention"
            value={natureIntervention}
            onChange={e => setNatureIntervention(e.target.value)}
          />
        </div>
      </div>

      {/* Lignes existantes (édition inline) */}
      <div className="mt-10 mx-14 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <h5 className="font-semibold">Etat des lieux</h5>

          <div className="flex items-center gap-2">
            <button
              className="w-[220px] py-3 text-sm text-blue-admin rounded-lg border border-white-admin"
              onClick={addLine}
              type="button"
            >
              + Ajouter une ligne
            </button>

            <button
              className="w-[160px] py-3 text-sm text-white bg-blue-admin rounded-lg border border-blue-admin"
              onClick={handleSave}
              type="button"
            >
              Enregistrer
            </button>
          </div>
        </div>

        {!materialsEdit?.length && (
          <p className="ml-2 my-3 italic text-red-error">
            Vous n'avez aucune ligne liée à ce bon !
          </p>
        )}

        {!!materialsEdit?.length && (
          <Table>
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Lieu</th>
                <th>Quantité</th>
                <th>Unité</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {materialsEdit.map((line, index) => (
                <tr key={index}>
                  <td className="border border-white border-r-8">
                    <input
                      className="mt-3 w-[320px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin inputFocus"
                      type="text"
                      placeholder="Désignation"
                      value={line.name}
                      onChange={e => changeLineField(index, 'name', e.target.value)}
                    />
                  </td>

                  <td className="border border-white border-x-8">
                    <input
                      className="mt-3 w-[360px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin input"
                      type="text"
                      placeholder="Lieu de l’intervention (optionnel)"
                      value={line.address}
                      onChange={e => changeLineField(index, 'address', e.target.value)}
                    />
                  </td>

                  <td className="border border-white border-x-8">
                    <input
                      className="mt-3 w-[160px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin input"
                      type="number"
                      inputMode="decimal"
                      placeholder="Quantité"
                      value={line.quantity}
                      onChange={e => changeLineField(index, 'quantity', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </td>

                  <td className="border border-white border-x-8">
                    <select
                      className="mt-3 w-[160px] rounded-lg py-4 px-3 border border-white-admin text-blue-admin input"
                      value={line.unit}
                      onChange={e => changeLineField(index, 'unit', e.target.value)}
                    >
                      {UNIT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="text-center border border-white border-x-8">
                    <button
                      type="button"
                      className="w-8 p-2 border rounded-lg border-white-admin"
                      onClick={() => askDeleteLine(index)}
                      title="Supprimer la ligne"
                    >
                      <img src={DeleteIcon} alt="delete icon" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {!!formError && <p className="text-red-error text-end mt-5 italic">{formError}</p>}
      </div>

      {/* Footer actions */}
      <div className="mt-16 py-4 flex justify-between items-center border-t border-white-admin">
        <button
          onClick={() => navigate(-1)}
          className="border rounded-lg cursor-pointer ml-14 border-white-admin px-5 py-3 text-blue-admin"
        >
          Retour
        </button>

        <button
          onClick={() => setShowModalDeleteCoupon(true)}
          className="border rounded-lg cursor-pointer ml-14 bg-red-error mr-14 px-5 py-3 text-white"
        >
          Supprimer le bon
        </button>
      </div>

      {/* Modal suppression ligne */}
      <Modal
        showModal={showModalDeleteLine}
        title="Ligne"
        text="Voulez-vous vraiment supprimer cette ligne du bon ?"
        image={DeleteIcon}
      >
        <button
          className="px-4 py-3 text-white rounded-lg cursor-pointer bg-red-error w-full"
          onClick={confirmDeleteLine}
        >
          Supprimer
        </button>
        <button
          className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-blue-admin mt-1 w-full"
          onClick={() => setShowModalDeleteLine(false)}
        >
          Annuler
        </button>
      </Modal>

      {/* Modal suppression bon */}
      <Modal
        showModal={showModalDeleteCoupon}
        title={`Bon d'intervention n°${preInter?.numero}`}
        text="Voulez-vous vraiment supprimer ce bon d'intervention ?"
        image={DeleteIcon}
      >
        <button
          className="px-4 py-3 text-white rounded-lg cursor-pointer bg-red-error w-full"
          onClick={callDeleteCoupon}
        >
          Supprimer
        </button>
        <button
          className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-blue-admin mt-1 w-full"
          onClick={() => setShowModalDeleteCoupon(false)}
        >
          Annuler
        </button>
      </Modal>
    </section>
  );
};

export default EditPrefillWorkOrder;